import { __rest } from '../../../../_virtual/_tslib.js';
import React from 'react';
import { useAutocompleteContext } from '../../state/autocomplete-context.js';
import { isFocused } from '../list-box.utils.js';
import { highlightText } from '../../../../helpers/highlight-text/index.js';
import * as reactUtils from '../../../../packages/react-utils/react-utils.js';
import Checkbox from '../../../../components/checkbox/checkbox.js';

function ListBoxOption(_a) {
  var {
      bem,
      id,
      option,
      renderOption,
      renderWidget,
      updateOptionSelection,
      onChange,
      indent = false,
      showControl = false,
      innerRef,
      selected = false,
      highlightOptions = 'both',
      highlightQuery
    } = _a,
    rest = __rest(_a, ["bem", "id", "option", "renderOption", "renderWidget", "updateOptionSelection", "onChange", "indent", "showControl", "innerRef", "selected", "highlightOptions", "highlightQuery"]);
  const ref = React.useRef();
  const {
    title,
    subtitle,
    value
  } = option;
  const autocompleteContext = useAutocompleteContext();
  const {
    state,
    dispatch
  } = autocompleteContext ? Object.assign({}, autocompleteContext) : {};
  const shouldHighlightTitle = highlightOptions === 'title' || highlightOptions === 'both';
  const shouldHighlightSubtitle = highlightOptions === 'subtitle' || highlightOptions === 'both';
  const optionNameId = "title-".concat(id);
  React.useEffect(() => {
    if (onChange) {
      onChange(selected, option);
    }
  }, [selected]);
  React.useEffect(() => {
    var _a, _b;
    if (ref.current && isFocused(option, state === null || state === void 0 ? void 0 : state.focusedOption)) {
      (_b = (_a = ref.current).scrollIntoView) === null || _b === void 0 ? void 0 : _b.call(_a, {
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [state === null || state === void 0 ? void 0 : state.focusedOption]);
  return /*#__PURE__*/React.createElement("li", Object.assign({
    id: id,
    role: "option",
    ref: node => {
      reactUtils.assignInnerRefWithComponentRef(node, innerRef, ref);
    },
    "aria-selected": selected,
    "aria-labelledby": optionNameId,
    "data-value": value,
    className: bem.e('option', {
      hover: isFocused(option, state === null || state === void 0 ? void 0 : state.focusedOption),
      selected,
      indent
    })
  }, rest, {
    onMouseEnter: event => {
      var _a;
      (_a = rest.onMouseEnter) === null || _a === void 0 ? void 0 : _a.call(rest, event, option);
      if (dispatch && !event.preventHCREventHandling) {
        // sets focus on hovered option and disables keyboard navigation flag
        dispatch({
          type: 'FOCUS_OPTION',
          option,
          kbNavigation: false,
          latestEvent: event
        });
      }
    },
    onMouseDown: event => {
      var _a;
      (_a = rest.onMouseDown) === null || _a === void 0 ? void 0 : _a.call(rest, event, option);
      if (!event.preventHCREventHandling) {
        // prevents focus from being triggered
        event.preventDefault();
      }
    },
    onClick: event => {
      var _a;
      (_a = rest.onClick) === null || _a === void 0 ? void 0 : _a.call(rest, event, option);
      if (!event.preventHCREventHandling) {
        updateOptionSelection(option, event);
      }
    },
    onKeyDown: event => {
      var _a;
      // jsx-a11y: non-interactive elements with click handlers must have keydown handlers too
      (_a = rest.onKeyDown) === null || _a === void 0 ? void 0 : _a.call(rest, event, option);
    }
  }), showControl && (/*#__PURE__*/React.createElement("div", {
    className: bem === null || bem === void 0 ? void 0 : bem.e('control'),
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    id: "control-".concat(id),
    role: "presentation",
    "aria-hidden": "true",
    "aria-label": title,
    value: value,
    checked: selected,
    small: true,
    tabIndex: -1,
    // this checkbox is just visual candy
    inert: "true",
    // prevents click from being called twice when clicking the checkbox
    onClick: event => {
      event.stopPropagation();
    },
    // this is here to silence React error for controlled components,
    // the event is handled in updateOptionSelection,
    onChange: () => {}
  }))), /*#__PURE__*/React.createElement("div", {
    id: optionNameId,
    className: bem === null || bem === void 0 ? void 0 : bem.e('option-name')
  }, renderOption ? renderOption({
    option,
    highlightQuery,
    shouldHighlightTitle,
    shouldHighlightSubtitle
  }) : (/*#__PURE__*/React.createElement("div", {
    className: bem === null || bem === void 0 ? void 0 : bem.e('title')
  }, /*#__PURE__*/React.createElement("div", null, shouldHighlightTitle ? highlightText(title, highlightQuery) : title), subtitle && (/*#__PURE__*/React.createElement("div", {
    className: bem === null || bem === void 0 ? void 0 : bem.e('subtitle')
  }, shouldHighlightSubtitle ? highlightText(subtitle, highlightQuery) : subtitle))))), renderWidget && (/*#__PURE__*/React.createElement("div", {
    className: bem === null || bem === void 0 ? void 0 : bem.e('widget')
  }, renderWidget())));
}

export { ListBoxOption };
